import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
  useCallback,
} from "react";
import { helpContentMap } from "../utils/helpContent";

export type HelpContent = {
  title: string;
  description: string;
  fields?: {
    [fieldId: string]: {
      label: string;
      description: string;
      indianContext?: string;
    };
  };
  indianContext?: string;
};

type HelpContextType = {
  isHelpEnabled: boolean;
  toggleHelp: () => void;
  showHelp: (screenId: string, fieldId?: string) => void;
  hideHelp: () => void;
  currentScreenId: string | null;
  currentFieldId: string | null;
  getHelpContent: (screenId: string, fieldId?: string) => HelpContent | null;
};

const HelpContext = createContext<HelpContextType | undefined>(undefined);

export const useHelpContext = () => {
  const context = useContext(HelpContext);
  if (!context) {
    throw new Error("useHelpContext must be used within HelpProvider");
  }
  return context;
};

type HelpProviderProps = {
  children: ReactNode;
};

export const HelpProvider: FC<HelpProviderProps> = ({ children }) => {
  const [isHelpEnabled, setIsHelpEnabled] = useState(true);
  const [currentScreenId, setCurrentScreenId] = useState<string | null>(null);
  const [currentFieldId, setCurrentFieldId] = useState<string | null>(null);

  const toggleHelp = useCallback(() => {
    setIsHelpEnabled((prev) => !prev);
  }, []);

  const showHelp = useCallback(
    (screenId: string, fieldId?: string) => {
      if (isHelpEnabled) {
        setCurrentScreenId(screenId);
        setCurrentFieldId(fieldId || null);
      }
    },
    [isHelpEnabled],
  );

  const hideHelp = useCallback(() => {
    setCurrentScreenId(null);
    setCurrentFieldId(null);
  }, []);

  const getHelpContent = useCallback(
    (screenId: string, fieldId?: string): HelpContent | null => {
      return helpContentMap[screenId] || null;
    },
    [],
  );

  const value: HelpContextType = {
    isHelpEnabled,
    toggleHelp,
    showHelp,
    hideHelp,
    currentScreenId,
    currentFieldId,
    getHelpContent,
  };

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
};
